import cx from 'classnames';
import { lowerCase, each } from 'lodash';
import React from 'react';
import { TransitionGroup } from 'react-transition-group';
import { Popover, Divider, Heading, CloseButton, Button, Text, Loader } from 'wix-style-react';

import { biLogSafe } from '../../../../../universal/biService';
import { DOMAINS_SUFFIXES_BY_COLUMNS } from '../../../../consts/domainsSuffixes';
import { FadeUp } from '@wix/branding-client-lib';
import Name from '../../Name';
import s from '../NamesListV3Desktop.scss';

class NamesContainer extends React.Component {
  shouldComponentUpdate(nextProps) {
    const { namesList: currentNamesList, currentIndexIsOpen: currentCurrentIndexIsOpen, loading: currentLoadingState } = this.props;
    const { namesList: nextNamesList, currentIndexIsOpen: nextCurrentIndexIsOpen, loading: nextLoadingState } = nextProps;
    return currentNamesList !== nextNamesList || currentCurrentIndexIsOpen !== nextCurrentIndexIsOpen || currentLoadingState !== nextLoadingState;
  }

  generateDomainBox = (nameForDomain, domainsInBox = [], colIndex) => {
    const { t } = this.props;
    const nameLength = nameForDomain.length;
    const isLongName = nameLength > 27;
    let nameParsedForDomain = nameForDomain;
    if (isLongName) {
      nameParsedForDomain = nameParsedForDomain.slice(0, 27);
    }
    let domainsTextSize = 'medium';
    if (nameLength > 22) {
      domainsTextSize = 'small';
    }
    return (
      <div className={s.domainBox}>
        {domainsInBox.map((domainSuffix, idx) => {
          const domainFullText = `${nameParsedForDomain}.${domainSuffix}`;
          return (
            <div key={`${colIndex}${idx}${nameParsedForDomain}`} className={s.domainRow}>
              <Text size={domainsTextSize} weight="bold">
                {domainFullText}
              </Text>
              <Button
                onClick={e => this.handleDomainClicked(e, domainFullText, nameParsedForDomain)}
                size="tiny"
                skin="standard"
                priority={colIndex === 0 && idx === 0 ? 'primary' : 'secondary'}
              >
                {t('BNG_NAMES_LIST_ITEM_CTA')}
              </Button>
            </div>
          );
        })}
      </div>
    );
  };

  handleDomainClicked = (e, domain, name) => {
    const { onDomainClicked, referralInfo } = this.props;
    biLogSafe(biLogger => {
      biLogger('businessNameGeneratorFinalActionClick', {
        origin: referralInfo,
        pageName: 'Names List',
        actionName: 'domain text',
        additionalInfo: JSON.stringify({ businessName: name, domainName: domain })
      });
    });
    onDomainClicked(e, domain);
  };

  createDomainSection = name => {
    const nameParsedForDomain = lowerCase(name).replace(/\s+/g, '');
    const domainColumns = [];
    each(DOMAINS_SUFFIXES_BY_COLUMNS, (batchOf3Domains, idx) => {
      domainColumns.push(this.generateDomainBox(nameParsedForDomain, batchOf3Domains, idx));
    });

    return (
      <div className={s.domains}>
        {domainColumns[0]}
        <Divider direction="vertical" />
        {domainColumns[1]}
        <Divider direction="vertical" />
        {domainColumns[2]}
      </div>
    );
  };

  wrapNameWithPopOver = (nameData = {}, index) => {
    const { t, currentIndexIsOpen, onNameLineClicked, onLikeClicked, referralInfo, industry } = this.props;
    const { text, isLiked, domain } = nameData;
    const industryText = industry ? industry.industryText : '';
    let popPlacement = 'bottom';
    if (index % 3 === 0) {
      popPlacement = 'bottom-start';
    } else if (index % 3 === 2) {
      popPlacement = 'bottom-end';
    }
    return (
      <Popover
        animate
        disableClickOutsideWhenClosed
        shown={currentIndexIsOpen === index}
        appendTo="window"
        onClick={() => {
          onNameLineClicked(index);
        }}
        onClickOutside={() => onNameLineClicked(-1)}
        showArrow
        placement={popPlacement}
        flip={false}
        className={cx(s.pop)}
        showDelay={100}
      >
        <Popover.Element>
          <Name
              key={`${index} ${text}`}
              industryText={industryText}
              name={text}
              isLiked={isLiked}
              t={t}
              index={index}
              onNameLineClicked={onNameLineClicked}
              onLikeClicked={onLikeClicked}
              domain={domain}
              referralInfo={referralInfo}
              expand={index === currentIndexIsOpen}
            />
        </Popover.Element>
        <Popover.Content>
          <div className={s.domainsSection}>
            <div className={s.header}>
              <div />
              <div className={s.title}>
                <Heading appearance="H3">{t('BNG_NAMES_LIST_ITEM_DOMAINS_TITLE')}</Heading>
              </div>
              <div className={s.closeButton}>
                <CloseButton onClick={() => onNameLineClicked(-1)} size="large" />
              </div>
            </div>
            {this.createDomainSection(text)}
          </div>
        </Popover.Content>
      </Popover>
    );
  };

  render() {
    const { namesList, t, loading, currentIndexIsOpen } = this.props;

    const numberOfNamesFoundTitle = t('BNG_NAMES_LIST_SUBTITLE_AMOUNT_ITEMS', { amount: namesList.length });

    return loading ? (
      <div className={s.loaderContainer}>
        <Loader key="loader" size="medium" />
        <span key="message" className={s.loaderMassage}>
          <Text> {t('GENERATING_NAMES_MESSAGE')}</Text>
        </span>
      </div>
    ) : (
      <>
        <div className={s.numberOfNamesTitle}>
          <Text size="small" secondary>
            {numberOfNamesFoundTitle}
          </Text>
        </div>
        <TransitionGroup key="transitionNames" component="div" className={s.namesTable}>
          {namesList.map((nameData, index) => {
            return (
              <FadeUp key={index + 'fade'} delay={index < 100 ? (index / 3) * 80 : 1000}>
                <div className={cx(s.nameWrapper, { [s.isShown]: currentIndexIsOpen === index, [s.showTransition]: currentIndexIsOpen === -1 })}>
                  {this.wrapNameWithPopOver(nameData, index)}
                </div>
              </FadeUp>
            );
          })}
        </TransitionGroup>
      </>
    );
  }
}

export default NamesContainer;
